import React, { useState, useEffect } from 'react'
import { X, User, Briefcase, Phone, MapPin, CreditCard, Building, Calendar, ChevronDown } from 'lucide-react'
import { jobService } from '../../services/jobService'
import branchService from '../../services/branchService'

const AddEmployeeModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    // Personal Information - Updated to match new endpoint
    fullName: '',
    gender: '', // male || female || other
    dateOfBirth: '',
    nicPassportDrivingLicense: '', // Updated field name
    contactNumber: '',
    emailAddress: '', // Updated field name
    address: '',
    
    // Emergency Contact - Updated structure
    emergencyContact: {
      name: '',
      relationship: '',
      contactNumber: ''
    },
    
    // Job Details - Updated to match new endpoint
    job: '', // ObjectId reference
    branch: '', // ObjectId reference  
    employeeType: 'full-time', // full-time || part-time || contract
    dateOfJoining: '',
    noOfLeaves: '', // Start empty instead of 0
    basicSalary: '', // Basic salary field
    serviceChargeRate: '', // Service charge rate percentage
    
    // Bank Account - Updated field names
    bankAccount: {
      accountName: '', // Updated field name
      accountNumber: '',
      bankName: '',
      bankBranch: '' // Updated field name
    },
    
    isActive: true // Updated field name
  })

  const [activeTab, setActiveTab] = useState('personal')
  const [jobs, setJobs] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [errors, setErrors] = useState({})
  const [toast, setToast] = useState(null)

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData()
    }
  }, [isOpen])

  // Show toast notification
  const showToast = (message, type = 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Validate form fields
  const validateForm = () => {
    const newErrors = {}

    // Personal Information validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required'
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required'
    } else if (formData.contactNumber.length !== 10) {
      newErrors.contactNumber = 'Contact number must be exactly 10 digits'
    }

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address'
    }

    if (!formData.nicPassportDrivingLicense.trim()) {
      newErrors.nicPassportDrivingLicense = 'NIC/Passport/Driving License is required'
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required'
    }

    // Job Details validation
    if (!formData.job) {
      newErrors.job = 'Please select a job'
    }

    if (!formData.branch) {
      newErrors.branch = 'Please select a branch'
    }

    if (!formData.dateOfJoining) {
      newErrors.dateOfJoining = 'Date of joining is required'
    }

    if (formData.noOfLeaves === '' || formData.noOfLeaves < 0) {
      newErrors.noOfLeaves = 'Number of leaves must be 0 or greater'
    }

    if (!formData.basicSalary || formData.basicSalary <= 0) {
      newErrors.basicSalary = 'Basic salary must be greater than 0'
    }

    if (formData.serviceChargeRate !== '' && (formData.serviceChargeRate < 0 || formData.serviceChargeRate > 100)) {
      newErrors.serviceChargeRate = 'Service charge rate must be between 0 and 100'
    }

    // Emergency Contact validation
    if (!formData.emergencyContact?.name?.trim()) {
      newErrors['emergencyContact.name'] = 'Emergency contact name is required'
    }

    if (!formData.emergencyContact?.relationship?.trim()) {
      newErrors['emergencyContact.relationship'] = 'Relationship is required'
    }

    if (!formData.emergencyContact?.contactNumber?.trim()) {
      newErrors['emergencyContact.contactNumber'] = 'Emergency contact number is required'
    } else if (formData.emergencyContact.contactNumber.length !== 10) {
      newErrors['emergencyContact.contactNumber'] = 'Emergency contact number must be exactly 10 digits'
    }

    // Bank Account validation
    if (!formData.bankAccount?.accountName?.trim()) {
      newErrors['bankAccount.accountName'] = 'Account holder name is required'
    }

    if (!formData.bankAccount?.accountNumber?.trim()) {
      newErrors['bankAccount.accountNumber'] = 'Account number is required'
    }

    if (!formData.bankAccount?.bankName?.trim()) {
      newErrors['bankAccount.bankName'] = 'Bank name is required'
    }

    if (!formData.bankAccount?.bankBranch?.trim()) {
      newErrors['bankAccount.bankBranch'] = 'Bank branch is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const fetchDropdownData = async () => {
    try {
      setLoadingData(true)
      
      // Fetch jobs and branches in parallel
      const [jobsResponse, branchesResponse] = await Promise.all([
        jobService.getAllJobs(),
        branchService.getAllBranches()
      ])

      console.log('Jobs response:', jobsResponse)
      console.log('Branches response:', branchesResponse)

      if (jobsResponse.success) {
        // Try different possible response structures
        let jobsData = jobsResponse.data.jobs || jobsResponse.data || []
        
        // If it's still not an array, try to extract it differently
        if (!Array.isArray(jobsData)) {
          jobsData = []
        }
        
        console.log('Jobs data:', jobsData)
        setJobs(jobsData)
      } else {
        console.error('Jobs fetch failed:', jobsResponse)
        setJobs([])
      }

      if (branchesResponse.success) {
        // Try different possible response structures
        let branchesData = branchesResponse.data.branches || branchesResponse.data || []
        
        // If it's still not an array, try to extract it differently
        if (!Array.isArray(branchesData)) {
          branchesData = []
        }
        
        console.log('Branches data:', branchesData)
        setBranches(branchesData)
      } else {
        console.error('Branches fetch failed:', branchesResponse)
        setBranches([])
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error)
      setJobs([])
      setBranches([])
    } finally {
      setLoadingData(false)
    }
  }

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }

    // Clear specific field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Prevent double submission
    if (loading) return
    
    // Validate form before submission
    if (!validateForm()) {
      // Find first error tab and switch to it
      const errorFields = Object.keys(errors)
      if (errorFields.length > 0) {
        const firstError = errorFields[0]
        if (firstError.startsWith('emergencyContact')) {
          setActiveTab('emergency')
        } else if (firstError.startsWith('bankAccount')) {
          setActiveTab('bank')
        } else if (['job', 'branch', 'dateOfJoining', 'noOfLeaves', 'basicSalary', 'employeeType'].includes(firstError)) {
          setActiveTab('job')
        } else {
          setActiveTab('personal')
        }
        
        showToast('Please fix the errors before submitting', 'error')
      }
      return
    }
    
    setLoading(true)
    
    try {
      const response = await onSubmit(formData)
      
      // Check if the response indicates success
      if (response && response.success === false) {
        // Handle backend validation errors
        showToast(response.message || 'Failed to create employee. Please check your input.', 'error')
        
        // Handle specific field errors from backend
        if (response.message && response.message.includes('email')) {
          setErrors(prev => ({
            ...prev,
            emailAddress: response.message
          }))
          setActiveTab('personal')
        }
        
        return // Don't reset form or close modal on error
      }
      
      // Only on successful creation - parent component handles modal closing
      resetForm()
      
    } catch (error) {
      console.error('Error submitting form:', error)
      
      // Handle different types of errors
      let errorMessage = 'Failed to create employee. Please try again.'
      
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || errorMessage
        
        // Handle specific field errors
        if (error.response.data.message && error.response.data.message.includes('email')) {
          setErrors(prev => ({
            ...prev,
            emailAddress: error.response.data.message
          }))
          setActiveTab('personal')
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      showToast(errorMessage, 'error')
      // Don't reset form on error - keep the data
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      // Personal Information - Updated to match new endpoint
      fullName: '',
      gender: '',
      dateOfBirth: '',
      nicPassportDrivingLicense: '',
      contactNumber: '',
      emailAddress: '',
      address: '',
      
      // Emergency Contact - Updated structure
      emergencyContact: {
        name: '',
        relationship: '',
        contactNumber: ''
      },
      
      // Job Details - Updated to match new endpoint
      job: '',
      branch: '',
      employeeType: 'full-time',
      dateOfJoining: '',
      noOfLeaves: '', // Start empty instead of 0
      basicSalary: '', // Basic salary field
      serviceChargeRate: '', // Service charge rate percentage
      
      // Bank Account - Updated field names
      bankAccount: {
        accountName: '',
        accountNumber: '',
        bankName: '',
        bankBranch: ''
      },
      
      isActive: true
    })
    setActiveTab('personal')
  }

  const handleClose = () => {
    // Reset form and errors when closing
    resetForm()
    setErrors({})
    setToast(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-md ${
          toast.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            <div className={`w-2 h-2 rounded-full mr-3 ${
              toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            }`} />
            {toast.message}
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Add New Employee</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tab Navigation - Fixed */}
        <div className="flex border-b bg-gray-50 flex-shrink-0">
          <button
            onClick={() => setActiveTab('personal')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'personal'
                ? 'border-orange-500 text-orange-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <User className="h-4 w-4" />
            <span>Personal Information</span>
          </button>
          <button
            onClick={() => setActiveTab('job')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'job'
                ? 'border-orange-500 text-orange-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Briefcase className="h-4 w-4" />
            <span>Job Details</span>
          </button>
          <button
            onClick={() => setActiveTab('emergency')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'emergency'
                ? 'border-orange-500 text-orange-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Phone className="h-4 w-4" />
            <span>Emergency Contact</span>
          </button>
          <button
            onClick={() => setActiveTab('bank')}
            className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'bank'
                ? 'border-orange-500 text-orange-600 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <CreditCard className="h-4 w-4" />
            <span>Bank Account</span>
          </button>
        </div>

        {/* Form Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
            {loadingData ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                  <p className="text-gray-500">Loading form data...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Personal Information Tab */}
                {activeTab === 'personal' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.fullName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors.fullName && (
                        <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Number (10 digits) *
                      </label>
                      <input
                        type="tel"
                        value={formData.contactNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                          handleInputChange('contactNumber', value)
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.contactNumber ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="1234567890"
                        maxLength="10"
                        required
                      />
                      {errors.contactNumber && (
                        <p className="mt-1 text-sm text-red-600">{errors.contactNumber}</p>
                      )}
                      
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.emailAddress}
                        onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.emailAddress ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors.emailAddress && (
                        <p className="mt-1 text-sm text-red-600">{errors.emailAddress}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        NIC/Passport/Driving License *
                      </label>
                      <input
                        type="text"
                        value={formData.nicPassportDrivingLicense}
                        onChange={(e) => handleInputChange('nicPassportDrivingLicense', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.nicPassportDrivingLicense ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors.nicPassportDrivingLicense && (
                        <p className="mt-1 text-sm text-red-600">{errors.nicPassportDrivingLicense}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Gender
                      </label>
                      <div className="relative">
                        <select
                          value={formData.gender}
                          onChange={(e) => handleInputChange('gender', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10"
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Address *
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.address ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors.address && (
                        <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Job Details Tab */}
                {activeTab === 'job' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Job * {loadingData && <span className="text-orange-500">(Loading...)</span>}
                      </label>
                      <div className="relative">
                        <select
                          value={formData.job}
                          onChange={(e) => handleInputChange('job', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10 ${
                            errors.job ? 'border-red-500' : 'border-gray-300'
                          }`}
                          required
                          disabled={loadingData}
                        >
                          <option value="">
                            {loadingData ? 'Loading jobs...' : 'Select Job'}
                          </option>
                          {!loadingData && jobs.length === 0 && (
                            <option value="" disabled>No jobs available</option>
                          )}
                          {jobs.map(job => (
                            <option key={job._id} value={job._id}>
                              {job.name || 'Untitled Job'}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                      </div>
                      {errors.job && (
                        <p className="mt-1 text-sm text-red-600">{errors.job}</p>
                      )}
                      {!loadingData && (
                        <p className="text-xs text-gray-500 mt-1">
                          {jobs.length} job{jobs.length !== 1 ? 's' : ''} available
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Branch * {loadingData && <span className="text-orange-500">(Loading...)</span>}
                      </label>
                      <div className="relative">
                        <select
                          value={formData.branch}
                          onChange={(e) => handleInputChange('branch', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10 ${
                            errors.branch ? 'border-red-500' : 'border-gray-300'
                          }`}
                          required
                          disabled={loadingData}
                        >
                          <option value="">
                            {loadingData ? 'Loading branches...' : 'Select Branch'}
                          </option>
                          {!loadingData && branches.length === 0 && (
                            <option value="" disabled>No branches available</option>
                          )}
                          {branches.map(branch => (
                            <option key={branch._id} value={branch._id}>
                              {branch.name || 'Unnamed Branch'}
                            </option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                      </div>
                      {errors.branch && (
                        <p className="mt-1 text-sm text-red-600">{errors.branch}</p>
                      )}
                      {!loadingData && (
                        <p className="text-xs text-gray-500 mt-1">
                          {branches.length} branch{branches.length !== 1 ? 'es' : ''} available
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Joining *
                      </label>
                      <input
                        type="date"
                        value={formData.dateOfJoining}
                        onChange={(e) => handleInputChange('dateOfJoining', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.dateOfJoining ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors.dateOfJoining && (
                        <p className="mt-1 text-sm text-red-600">{errors.dateOfJoining}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Leaves (Per Month) *
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.noOfLeaves === '' ? '' : formData.noOfLeaves}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : parseInt(e.target.value) || 0
                          handleInputChange('noOfLeaves', value)
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.noOfLeaves ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter number of leaves"
                        required
                      />
                      {errors.noOfLeaves && (
                        <p className="mt-1 text-sm text-red-600">{errors.noOfLeaves}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Basic Salary *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.basicSalary}
                        onChange={(e) => handleInputChange('basicSalary', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.basicSalary ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="0.00"
                        required
                      />
                      {errors.basicSalary && (
                        <p className="mt-1 text-sm text-red-600">{errors.basicSalary}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Service Charge Rate (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.serviceChargeRate}
                        onChange={(e) => handleInputChange('serviceChargeRate', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors.serviceChargeRate ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="0"
                      />
                      {errors.serviceChargeRate && (
                        <p className="mt-1 text-sm text-red-600">{errors.serviceChargeRate}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Employee Type
                      </label>
                      <div className="relative">
                        <select
                          value={formData.employeeType}
                          onChange={(e) => handleInputChange('employeeType', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none pr-10"
                        >
                          <option value="full-time">Full Time</option>
                          <option value="part-time">Part Time</option>
                          <option value="contract">Contract</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                      </div>
                    </div>

                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Active Status
                      </label>
                      <div className="flex items-center space-x-3">
                        <span className={`text-sm font-medium ${!formData.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                          Inactive
                        </span>
                        <button
                          type="button"
                          onClick={() => handleInputChange('isActive', !formData.isActive)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                            formData.isActive ? 'bg-orange-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.isActive ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className={`text-sm font-medium ${formData.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                          Active
                        </span>
                      </div>
                    </div> */}
                  </div>
                )}                {/* Emergency Contact Tab */}
                {activeTab === 'emergency' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Emergency Contact Name *
                      </label>
                      <input
                        type="text"
                        value={formData.emergencyContact?.name}
                        onChange={(e) => handleInputChange('emergencyContact.name', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors['emergencyContact.name'] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors['emergencyContact.name'] && (
                        <p className="mt-1 text-sm text-red-600">{errors['emergencyContact.name']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Relationship *
                      </label>
                      <input
                        type="text"
                        value={formData.emergencyContact?.relationship}
                        onChange={(e) => handleInputChange('emergencyContact.relationship', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors['emergencyContact.relationship'] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="e.g., Father, Mother, Spouse"
                        required
                      />
                      {errors['emergencyContact.relationship'] && (
                        <p className="mt-1 text-sm text-red-600">{errors['emergencyContact.relationship']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Number (10 digits) *
                      </label>
                      <input
                        type="tel"
                        value={formData.emergencyContact?.contactNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                          handleInputChange('emergencyContact.contactNumber', value)
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors['emergencyContact.contactNumber'] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="1234567890"
                        maxLength="10"
                        required
                      />
                      {errors['emergencyContact.contactNumber'] && (
                        <p className="mt-1 text-sm text-red-600">{errors['emergencyContact.contactNumber']}</p>
                      )}
                     
                    </div>
                  </div>
                )}

                {/* Bank Account Tab */}
                {activeTab === 'bank' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Holder Name *
                      </label>
                      <input
                        type="text"
                        value={formData.bankAccount?.accountName}
                        onChange={(e) => handleInputChange('bankAccount.accountName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors['bankAccount.accountName'] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors['bankAccount.accountName'] && (
                        <p className="mt-1 text-sm text-red-600">{errors['bankAccount.accountName']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Number *
                      </label>
                      <input
                        type="text"
                        value={formData.bankAccount?.accountNumber}
                        onChange={(e) => handleInputChange('bankAccount.accountNumber', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors['bankAccount.accountNumber'] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors['bankAccount.accountNumber'] && (
                        <p className="mt-1 text-sm text-red-600">{errors['bankAccount.accountNumber']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bank Name *
                      </label>
                      <input
                        type="text"
                        value={formData.bankAccount?.bankName}
                        onChange={(e) => handleInputChange('bankAccount.bankName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors['bankAccount.bankName'] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors['bankAccount.bankName'] && (
                        <p className="mt-1 text-sm text-red-600">{errors['bankAccount.bankName']}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Branch Name *
                      </label>
                      <input
                        type="text"
                        value={formData.bankAccount?.bankBranch}
                        onChange={(e) => handleInputChange('bankAccount.bankBranch', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                          errors['bankAccount.bankBranch'] ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {errors['bankAccount.bankBranch'] && (
                        <p className="mt-1 text-sm text-red-600">{errors['bankAccount.bankBranch']}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer - Fixed */}
          <div className="flex items-center justify-between p-6 border-t bg-gray-50 flex-shrink-0">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('isActive', !formData.isActive)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
                    formData.isActive ? 'bg-orange-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || loadingData}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Employee'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddEmployeeModal
